// @flow

import React from 'react';
import { useIntl } from 'react-intl';
import { InputV2, InputConsumer, DateTimeInput } from '@catalytic/catalytic-ui';
import { types } from '@catalytic/react-view';
import useFieldContext from './FieldContext';
import {
  toLocaleAwareDateFormat,
  toMomentDateTimeFormat,
  useHandleBlur,
  useHandleChange
} from './ViewHelpers';

const DateTimeView = ({
  node: getNode,
  schema: getSchema,
  setValue,
  value
}: types.DateTimeView) => {
  const { year, month, day, hour, minute, hour12 } = getNode() || {};
  const dateFormat = toLocaleAwareDateFormat({ year, month, day });
  const timeFormat = toMomentDateTimeFormat({ hour, minute, hour12 });
  const schema = getSchema();
  const fieldContext = useFieldContext();
  const {
    className,
    contextValues,
    description,
    disabled,
    displayErrors,
    displayName,
    embedded,
    example,
    fieldName,
    hasValidation,
    id,
    name,
    onBlur,
    onChange,
    readOnly,
    required,
    type
  } = fieldContext || {};
  const handleBlur = useHandleBlur({ onBlur, setValue, type });
  const handleChange = useHandleChange({ onChange, setValue, type });
  const intl = useIntl();
  const locale = intl?.locale;

  return (
    <InputV2
      className={className}
      contextValues={contextValues}
      description={description}
      disabled={disabled}
      displayErrors={displayErrors}
      example={example}
      fieldName={fieldName}
      hasValidation={hasValidation}
      id={id}
      name={name}
      onBlur={handleBlur}
      onChange={handleChange}
      readOnly={readOnly}
      schema={schema}
      title={displayName}
      type={type}
      value={value}
    >
      <InputConsumer>
        {props => (
          <DateTimeInput
            dateFormat={dateFormat}
            defaultValue={value}
            embedded={embedded}
            id={id}
            locale={locale}
            name={name}
            required={required}
            timeFormat={timeFormat}
            {...props}
          />
        )}
      </InputConsumer>
    </InputV2>
  );
};
DateTimeView.displayName = 'DateTimeView';

export default React.memo<types.DateTimeView>(DateTimeView);
